import type { CSSProperties } from 'react';
import {
  WS_COLOR,
  WS_CONTROL,
  WS_FONT,
  WS_RADIUS,
  WS_SPACE,
  WS_STATUS,
  WS_TYPE,
  type WsTone,
} from '@platform/ui-kit/workspace';

/**
 * Program Planning on the Decision Workspace design (owner ruling 2026-10-21: "apply UI
 * design kit to all of Program Planning & Roadmap").
 *
 * The ONE place this module reads the kit's values. Screens import from here, never type a
 * colour or a size: every value below comes from `@platform/ui-kit/workspace` tokens
 * (DESIGN.md), so a change to the kit moves Program Planning with it.
 *
 * The only colours NOT from the kit are disposition colours, which stay on the Disposition
 * Matrix (`../dispositionPalette`, CLAUDE.md §1.9.3 rule 3; owner ruling 2026-10-21).
 */

export { WS_COLOR, WS_STATUS, WS_TYPE, WS_RADIUS, WS_SPACE, WS_CONTROL, WS_FONT };
export type { WsTone };

type TypeRole = keyof typeof WS_TYPE;

/** A type role as inline style: `{...wsType('label')}` → 13px / 20px / 500. */
export function wsType(role: TypeRole): CSSProperties {
  const [size, line, weight] = WS_TYPE[role];
  return {
    fontSize: `${size}px`,
    lineHeight: `${line}px`,
    fontWeight: weight,
    ...(role === 'mono' ? { fontFamily: WS_FONT.mono } : null),
  };
}

/** A measured status pair as inline style — chips, banners, badges. Never split a pair. */
export function wsTone(tone: WsTone): CSSProperties {
  const pair = WS_STATUS[tone];
  return { background: pair.fill, color: pair.ink };
}

/** The chip shape (CLAUDE.md 1.9.2): 8px corners, no border, 13/500. Pair with `wsTone`. */
export const WS_CHIP: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  minHeight: 24,
  padding: '0 10px',
  borderRadius: WS_RADIUS.chip,
  border: 'none',
  fontSize: `${WS_TYPE.label[0]}px`,
  lineHeight: `${WS_TYPE.label[1]}px`,
  fontWeight: WS_TYPE.label[2],
  whiteSpace: 'nowrap',
};

/** Card: white surface, 1px border, 12px corners, flat (DESIGN.md Elevation). */
export const WS_CARD: CSSProperties = {
  background: WS_COLOR.surface,
  border: `1px solid ${WS_COLOR.border}`,
  borderRadius: WS_RADIUS.card,
  boxShadow: 'none',
};

/**
 * The kit tokens as CSS custom properties, set on `.pp-app` by PpScreen so the module's
 * stylesheet (`pp-workspace.css`) reads the same values the TypeScript does.
 */
export function wsCssVars(): CSSProperties {
  const v: Record<string, string> = {
    '--ws-font': WS_FONT.sans,
    '--ws-mono': WS_FONT.mono,
    '--ws-bg': WS_COLOR.background,
    '--ws-surface': WS_COLOR.surface,
    '--ws-surface-subtle': WS_COLOR.surfaceSubtle,
    '--ws-text': WS_COLOR.text,
    '--ws-text-muted': WS_COLOR.textMuted,
    '--ws-border': WS_COLOR.border,
    '--ws-field-outline': WS_COLOR.fieldOutline,
    '--ws-primary': WS_COLOR.primary,
    '--ws-on-primary': WS_COLOR.onPrimary,
    '--ws-focus': WS_COLOR.focus,
    '--ws-tonal': WS_COLOR.tonal,
    '--ws-on-tonal': WS_COLOR.onTonal,
    '--ws-tonal-hover': WS_COLOR.tonalHover,
    '--ws-destructive': WS_COLOR.destructive,
    '--ws-scrim': WS_COLOR.scrim,
    '--ws-shadow-menu': WS_COLOR.menuShadow,
    '--ws-r-chip': `${WS_RADIUS.chip}px`,
    '--ws-r-field': `${WS_RADIUS.field}px`,
    '--ws-r-card': `${WS_RADIUS.card}px`,
    '--ws-r-dialog': `${WS_RADIUS.dialog}px`,
    '--ws-r-button': `${WS_RADIUS.button}px`,
    '--ws-control-h': `${WS_CONTROL.height}px`,
    '--ws-row-h': `${WS_CONTROL.rowHeight}px`,
  };
  for (const [tone, pair] of Object.entries(WS_STATUS)) {
    v[`--ws-${tone}`] = pair.fill;
    v[`--ws-on-${tone}`] = pair.ink;
  }
  for (const [role, [size, line, weight]] of Object.entries(WS_TYPE)) {
    const key = role.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
    v[`--ws-fs-${key}`] = `${size}px`;
    v[`--ws-lh-${key}`] = `${line}px`;
    v[`--ws-fw-${key}`] = String(weight);
  }
  return v as CSSProperties;
}
